import queryString from 'query-string'
import React, {Ref, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {defineMessages, FormattedMessage, FormattedHTMLMessage, useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {TransitionGroup} from 'react-transition-group'
import {ICar} from '../../../models/Car'
import {IPublicView} from '../../../models/PublicView'
import {IStation} from '../../../models/Station'
import ExternalLink from '../../assets/icons/ExternalLink.svg'
import Question from '../../assets/icons/vanshare/Question.svg'
import {
  areaMessages,
  areaPageTitleWithOptionsMessages,
  areaPageDescriptionMessages,
  carAreaPageTitleWithOptionMessages,
  carAreaPageDescriptionMessages,
  carConditionTypeMessages,
  optionMessages,
  freeFacilityListMsg,
  nearbyFacilityListMsg,
  nonFreeFacilityListMsg,
  carFreeFacilityMessages,
  carNonFreeFacilityMessages
} from '../../config/commonMessages'
import {
  areaBounds,
  AreaType,
  CarConditionType,
  carConditionTypeList,
  carStationListPageSize,
  carStationMapPageSize,
  CURRENT_LOCATION,
  defaultCenter,
  defaultMapZoomLevel,
  defaultMapZoomOutLevel,
  getPrefecture,
  japanBound,
  japanLatLng,
  latEps,
  ListViewType,
  lngEps,
  Locale,
  urlPrefixes,
  prefectureBounds,
  prefectureToArea,
  PrefectureType,
  StationType, WHOLE_LOCATION, carTypes,
} from '../../config/constants'
import {usePublicViews} from '../../helpers/cachedRequest'
import {getGlobalEnv} from '../../helpers/coreUtil'
import {monthAndDay} from '../../helpers/dateUtil'
import {publicViewSEO} from '../../helpers/publicView'
import {stationSEO} from '../../helpers/station'
import FadeTransition from '../common/FadeTransition'
import Footer from '../common/Footer'
import LocaleText from '../common/LocaleText'
import Breadcrumb from '../containers/Breadcrumb'
import {NavBarPadding} from '../containers/Container'
import {RouterContext, StaticStateContext} from '../containers/Context'
import {IRouteComponentProps} from '../containers/DataResolver'
import {useIsSsr, useLocale, usePrev, useQuery, useQueryObj, useSEO, useUnmountedRef, useUserType} from '../hocs/hooks'
import Maps from '../maps/Maps'
import MapsCars from '../maps/MapsCars'
import MapsPublicViews from '../maps/MapsPublicViews'
import MapsStations from '../maps/MapsStations'
import {getCurrentPosition, getGoogleMapsClass, GoogleMapControlPosition, IMaps} from '../maps/mapsUtil'
import BasicSearch, {ISearchForm, stationCarQueryToValues, stationCarValuesToQuery} from '../user/cars/BasicSearch'
import CarsSearch from '../user/cars/CarsSearch'
import CarsSearchInput from '../user/cars/CarsSearchInput'
import {checkAreaOrPrefecture} from '../user/cars/CoverPC'
import MapListNavBar, {ListMode} from '../user/stations/MapListNavbar'
import StationsSearch from '../user/stations/StationsSearch'
import StationsSearchInput from '../user/stations/StationsSearchInput'
import ListView from './list/ListView'
import {MapSearchFilterPanel, ToggleListModePanel} from './MapButtons'
import TagButtons from './TagButtons'
import styles from './StationCarList.scss'
import { isNativeWebview, isNativeWebviewVersion2 } from '../../helpers/reactNativeWebview'
import { II18nMessage } from '../../reducers/i18n'
import Mobiinn from './Mobiinn'

const stationsStateFromProps = (
  {area: paramArea, map, station}: {area?: AreaType, map?: string, station?: string},
  queryArea: AreaType,
): {
  area?: AreaType
  station?: string
  publicView?: string
} => {
  const state: {
    station?: string
    area?: AreaType
    publicView?: string
  } = {
    area: map && station ? queryArea : paramArea,
    station: undefined,
    publicView: undefined
  }
  if (paramArea && map) {
    if (map === 'map') state.station = station
    else state.publicView = station
  }
  return state
}

const messages = defineMessages({
  japanSearchDescription: {
    id: 'stations.japanDescription',
    defaultMessage: 'Carstay is a camping accommodation service that connects campers who need a place for their RVs or tents, and hosts who can provide parking spaces or experience tours in Japan.'
  },
  japanSearchTitle: {
    id: 'stations.japanSearchTitle',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Japan'
  },
  japanSearchTitleWithOptions: {
    id: 'stations.japanSearchTitleWithOptions',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Japan'
  },
  carJapanSearchTitle: {
    id: 'car.japanSearchTitle',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Japan'
  },
  carJapanSearchTitleWithOptions: {
    id: 'car.japanSearchTitleWithOptions',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Japan'
  },
  carJapanSearchDescription: {
    id: 'car.japanSearchDescription',
    defaultMessage: 'Search results for camping car in Japan. Carstay is a camping accommodation service that connects campers who need a place for their RVs or tents,and hosts who can provide parking spaces or experience tours.'
  },
  stationPrefectureTitle: {
    id: 'areaTitles.template',
    defaultMessage: 'Search results for campsites and overnight spots in {prefecture}｜Camping spot and overnight spot sharing service [CarStay].'
  },
  stationPrefectureTitleWithOptions: {
    id: 'areaTitles.templateWithOptions',
    defaultMessage: 'Search results for campsites and overnight spots in {prefecture}｜Camping spot and overnight spot sharing service [CarStay].'
  },
  carPrefectureTitle: {
    id: 'carPrefectureTitles.template',
    defaultMessage: 'Search results for camping car in {prefecture}｜Discover Amazing Experiences while Driving in Japan'
  },
  carPrefectureTitleWithOptions: {
    id: 'carPrefectureTitles.templateWithOptions',
    defaultMessage: 'Search results for camping car in {prefecture}｜Discover Amazing Experiences while Driving in Japan'
  },
  carPrefectureDescription: {
    id: 'carPrefectureDescriptions.template',
    defaultMessage: 'Search results for camping car in {prefecture}. Carstay is a camping accommodation service that connects campers who need a place for their RVs or tents,and hosts who can provide parking spaces or experience tours.'
  },
  stationSearchCurrentLocation: {
    id: 'stationsSearch.currentLocation',
    defaultMessage: 'Current location'
  },
  carCurrentLocationSearchTitle: {
    id: 'car.currentLocationSearchTitle',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Current Location'
  },
  carCurrentLocationSearchTitleWithOptions: {
    id: 'car.currentLocationSearchTitleWithOptions',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Current Location'
  },
  carCurrentLocationSearchDescription: {
    id: 'car.currentLocationSearchDescription',
    defaultMessage: 'Search results for camping car nearby. Carstay is a camping accommodation service that connects campers who need a place for their RVs or tents,and hosts who can provide parking spaces or experience tours.'
  },
  stationCurrentLocationSearchTitle: {
    id: 'stations.currentLocationSearchTitle',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Current Location'
  },
  stationCurrentLocationSearchTitleWithOptions: {
    id: 'stations.currentLocationSearchTitleWithOptions',
    defaultMessage: 'Carstay｜Discover Amazing Experiences while Driving in Current Location'
  },
  switchModeLabel: {
    id: 'car.switchMode',
    defaultMessage: 'Switch to list view'
  }
})

export const getAreaLink = (
  locale: Locale,
  stationType: StationType,
  area: string,
  paramsArea = area,
  facilityMessage: String = ''
) => <Link to={`/${locale}/${stationType === StationType.car ? 'car-list' : 'stations'}/${checkAreaOrPrefecture(paramsArea)}/`}>
  {paramsArea === CURRENT_LOCATION
    ? stationType === StationType.car
      ? <FormattedMessage id="breadcrumb.carsCurrentLocationWithOption" values={{option: facilityMessage}} defaultMessage="Rental car share camping car near the current location" />
      : <FormattedMessage id="breadcrumb.stationsCurrentLocation" defaultMessage="Staying overnight in a car near the current location" />
    : paramsArea === WHOLE_LOCATION
      ? stationType === StationType.car
        ? <FormattedMessage id="breadcrumb.carsAllCountryWithOption" values={{option: facilityMessage}} defaultMessage="Rental Car Share Camping Cars Nationwide" />
        : <FormattedMessage id="breadcrumb.stationsAllCountry" defaultMessage="Staying overnight in a car nationwide" />
      : area
        ? stationType === StationType.car
          ? <FormattedMessage id="breadcrumb.carsAreaWithOption" values={{option: facilityMessage, area: <FormattedMessage {...areaMessages[paramsArea as AreaType]}/>}} defaultMessage="Rental Car Share Camping Cars in {area}"/>
          : <FormattedMessage id="breadcrumb.stationsArea" defaultMessage="Staying overnight in {area}" values={{area: <FormattedMessage {...areaMessages[paramsArea as AreaType]}/>}} />
        : <FormattedMessage id="navbar.vs" defaultMessage="Search a car" />
  }
</Link>

export const StationCarList = (stationType: StationType) => (
  {
    match,
    history,
    resolvedData: {total, stations, cars, keyCanonical},
    loading
  }: IRouteComponentProps<
      {
        total: number,
        stations: ReadonlyArray<IStation>,
        cars: ReadonlyArray<ICar>,
        keyCanonical: string,
      },
      {
        area: string,
        prefecture?: PrefectureType
      }
  >
) => {
  const queryObj = useQueryObj(stationCarQueryToValues, stationCarValuesToQuery)
  const queryObjRef = useRef(queryObj)
  useEffect(() => {
    queryObjRef.current = queryObj
  }, [queryObj])
  const query = useQuery(stationCarQueryToValues)
  const locale = useLocale()
  let { area: paramsArea, prefecture: paramsPrefecture } = match.params
  const {checkIn, checkOut, mode, swLat, swLng, zoom, keyword, longRental, airConditioner, page, carCompany} = query
  // validate area
  if (paramsArea !== CURRENT_LOCATION && paramsArea !== WHOLE_LOCATION && !areaMessages[paramsArea as AreaType]) {
    paramsArea = WHOLE_LOCATION
  }
  const area = paramsArea

  const prefecture = paramsPrefecture
  let {neLat, neLng} = query
  // In order to avoid the negative latitude and longtitude
  if (swLat && neLat && parseFloat(swLat) > parseFloat(neLat)) neLat = (parseFloat(neLat) + 80).toString()
  if (swLng && neLng && parseFloat(swLng) > parseFloat(neLng)) neLng = (parseFloat(neLng) + 360).toString()
  const [searchOpen, setSearchOpen] = useState(false)
  const [basicSearchOpen, setBasicSearchOpen] = useState(false)
  const closeSearch = useCallback(() => setSearchOpen(false), [])
  const closeBasicSearch = useCallback(() => setBasicSearchOpen(false), [])
  const openAdvancedSearch = useCallback(() => setSearchOpen(true), [])
  const openBasicSearch = useCallback(() => setBasicSearchOpen(true), [])
  const queryRef = useRef({})
  const urlParamQueryStringRef = useRef('')
  useEffect(() => {
    queryRef.current = query
    const { page: _page, ...otherQueries } = query
    const urlParamQuery = queryString.stringify(otherQueries)
    urlParamQueryStringRef.current = urlParamQuery ? `?${urlParamQuery}` : ''
  }, [query])
  const pageSize = mode === ListMode.map ? carStationMapPageSize : carStationListPageSize
  const facilityMessage = useRef('')
  const onSearchSubmit = useCallback(({area, prefecture, checkIn, checkOut}: ISearchForm) => {
    const path = `/${locale}/\
${stationType === StationType.car ? 'car-list' : 'stations'}/${checkAreaOrPrefecture(area)}/`
    const stationCarQuery = {
      ...queryRef.current,
      mode,
      checkIn,
      checkOut,
      page: 0,
      zoom: undefined
    }
    history.push(queryObjRef.current({ ...stationCarQuery }, {}, path))
  }, [history, locale, mode])
  const isSSR = useIsSsr()
  useEffect(() => {
    if (!isSSR) window.scroll({top: 0, left: 0, behavior: 'smooth'})
  }, [isSSR])
  const [maps, setMaps] = useState()
  const initedRef = useRef(false)
  const prevPrefecture = usePrev(prefecture)
  const currPrefectureRef = useRef(prefecture)
  const prevArea = usePrev(area)
  const currAreaRef = useRef(area)
  const unmountedRef = useUnmountedRef()
  
  const getInitialCenter = useCallback(bound => bound
    ? ({
      lat: (bound.north + bound.south) / 2,
      lng: (bound.east + bound.west) / 2
    }) : undefined, [])
  const initialCenter = (() => {
    if (prefecture && stationType === StationType.car) {
      return getInitialCenter(prefectureBounds[prefecture] || japanBound)
    }
    if (prefecture && stationType !== StationType.car) {
      return getInitialCenter(prefectureBounds[prefecture])
    }
    if (area) {
      return getInitialCenter(areaBounds[area] ? areaBounds[area] : japanBound)
    }
  })()
  const initialZoom = area ? 6 : prefecture ? 8 : 5
  const mapsZoomLevel = (area && area === CURRENT_LOCATION)
    ? defaultMapZoomLevel
    : area
      ? 6
      : (prefecture && prefecture === CURRENT_LOCATION)
        ? defaultMapZoomLevel
        : prefecture
          ? 8
          : 5
  const changeMapBounds = useCallback(async (maps: IMaps) => {
    const bounds = maps.getBounds()
    if (bounds) {
      const ne = bounds.getNorthEast().toJSON()
      const sw = bounds.getSouthWest().toJSON()
      let pos = null
      try {
        pos = await getCurrentPosition()
      } catch (e) {}
      queryObjRef.current({
        ...query,
        page: 0,
        neLat: ne.lat,
        neLng: ne.lng,
        swLat: sw.lat,
        swLng: sw.lng,
        zoom: maps.getZoom(),
        currentLat: pos ? pos.lat : undefined,
        currentLng: pos ? pos.lng : undefined,
      })
    }
  }, [history, query])
  const [refreshCustomizedButtons, setRefreshCustomizedButtons] = useState(false)
  const showSelectStationRef = useRef(true)
  useEffect(() => {
    if (prevArea !== area || prevPrefecture !== prefecture) showSelectStationRef.current = true
  }, [area, prefecture, prevArea, prevPrefecture])
  const [ggMaptitlesLoaded, setGgMapTitlesLoaded] = useState(false)
  useEffect(() => {
    if (maps) {
      const GoogleMaps = getGoogleMapsClass()
      const googleMapsEvents = ['tilesloaded'].map((eventKey: string) => GoogleMaps.event.addListener(
        maps,
        eventKey,
        () => setTimeout(() => setGgMapTitlesLoaded(true), 1000)
      ))
      return () => {
        for (
          const event of googleMapsEvents
        ) GoogleMaps.event.removeListener(event)
      }
    }
  }, [maps])
  const {hash} = useContext(RouterContext)!.location
  useEffect(() => {
    const stationList = stationType === StationType.car ? cars : stations
    if (ggMaptitlesLoaded && showSelectStationRef.current) {
      if (hash.startsWith('#')) {
        const stationId = hash.substr(1)
        for (const station of stationList) {
          if (String(station._id) === stationId) {
            setSelectStation(station)
            return
          }
        }
      }
      if (stationList.length) setSelectStation(stationList[0])
      else setSelectStation(undefined)
      showSelectStationRef.current = false
    }
  }, [stations, cars, ggMaptitlesLoaded, hash])
  const changeMapQueryRef = useRef('')
  useEffect(() => {
    const newMapQuery = JSON.stringify({
      ...query,
      currentLat: undefined,
      currentLng: undefined,
      neLat: undefined,
      neLng: undefined,
      swLat: undefined,
      swLng: undefined,
      zoom: undefined
    })
    if (changeMapQueryRef.current !== newMapQuery) {
      setRefreshCustomizedButtons(true)
      changeMapQueryRef.current = newMapQuery
    }
  }, [query])
  useEffect(() => {
    if (mode !== ListMode.map) {
      // eslint-disable-next-line no-new
      new Promise(async () => {
        const queryValues = {
          ...queryRef.current,
          neLat: undefined,
          neLng: undefined,
          swLat: undefined,
          swLng: undefined,
          currentLat: undefined,
          currentLng: undefined,
          zoom: undefined
        }
        if (area === CURRENT_LOCATION || paramsArea === CURRENT_LOCATION || prefecture === CURRENT_LOCATION) {
          let pos = defaultCenter
          try {
            pos = await getCurrentPosition()
          } catch (e) {}
          if (unmountedRef.current || !pos) return
          const {lat, lng} = pos as {lat: number, lng: number}
          queryValues.neLat = lat + latEps
          queryValues.neLng = lng + lngEps
          queryValues.swLat = lat - latEps
          queryValues.swLng = lng - lngEps
          queryValues.currentLat = lat
          queryValues.currentLng = lng
        }
        history.push(queryObjRef.current(queryValues))
      })
    }
  }, [mode, area, prefecture, paramsArea, history, unmountedRef])
  const prevMode = usePrev(mode)
  const mapsFitBounds = useCallback(bounds => {
    if (maps) {
      const GoogleMaps = getGoogleMapsClass()
      const neLat = bounds.north
      const neLong = bounds.east
      const swLat = bounds.south
      const swLong = bounds.west

      const boundingBoxPerc = 0.15
      const mapWidth = neLong - swLong
      const mapHeight = neLat - swLat

      const ne = new GoogleMaps.LatLng(neLat - mapHeight * boundingBoxPerc, neLong - mapWidth * boundingBoxPerc)
      const sw = new GoogleMaps.LatLng(swLat + mapHeight * boundingBoxPerc, swLong + mapWidth * boundingBoxPerc)
      maps.fitBounds(new GoogleMaps.LatLngBounds(sw, ne))
    }
  }, [maps])
  const mapsInit = useCallback(async (maps: IMaps) => {
    const inited = initedRef.current
    initedRef.current = true
    setMaps(maps)
    if (!history?.location?.search?.includes('page=')) {
      changeMapBounds(maps)
    }
    if (!inited || area !== prevArea || prefecture !== prevPrefecture || mode !== prevMode) {
      currAreaRef.current = area
      currPrefectureRef.current = prefecture
      if (area === CURRENT_LOCATION) {
        let pos = japanLatLng
        try {
          pos = await getCurrentPosition()
        } catch (e) { }
        if (unmountedRef.current) return
        if (pos) {
          maps.panTo(pos)
          maps.setZoom(pos === japanLatLng ? 5 : mapsZoomLevel)
        }
      } else if (prefectureBounds[prefecture]) {
        mapsFitBounds(prefectureBounds[prefecture])
      } else if (areaBounds[area]) mapsFitBounds(areaBounds[area])
      else {
        maps.panTo(japanLatLng)
        maps.setZoom(mapsZoomLevel)
      }
    }
  }, [history, changeMapBounds, area, prevArea, prefecture, prevPrefecture, mode, prevMode, mapsFitBounds, unmountedRef, mapsZoomLevel])
  useEffect(() => {
    // only call on the first load
    if (maps) {
      if (stationType === StationType.car) {
        mapsFitBounds(prefectureBounds[currPrefectureRef.current] || areaBounds[currAreaRef.current] || japanBound)
      } else mapsFitBounds(prefectureBounds[currPrefectureRef.current] || japanBound)
    }
  }, [maps, mapsFitBounds])

  useEffect(() => {
    if (area !== prevArea || prefecture !== prevPrefecture) {
      mapsFitBounds(prefectureBounds[currPrefectureRef.current] || areaBounds[area] || japanBound)
    }
  }, [area, maps, mapsFitBounds, prefecture, prevArea, prevPrefecture])
  useEffect(() => {
    if (maps) {
      const GoogleMaps = getGoogleMapsClass()
      const googleMapsEvents = ['dragend', 'zoom_changed'].map((eventKey: string) => GoogleMaps.event.addListener(
        maps,
        eventKey,
        () => changeMapBounds(maps)
      ))
      return () => {
        for (
          const event of googleMapsEvents
        ) GoogleMaps.event.removeListener(event)
      }
    }
  }, [maps, changeMapBounds])
  const toggleListMode = useCallback(() => {
    history.push(queryObj({
      ...queryRef.current,
      mode: mode === ListMode.map ? undefined : ListMode.map,
      page: 0,
      neLat: undefined,
      neLng: undefined,
      swLat: undefined,
      swLng: undefined,
      zoom: undefined,
      currentLat: undefined,
      currentLng: undefined
    }))
    currAreaRef.current = area
    currPrefectureRef.current = prefecture
  }, [history, queryObj, mode, area, prefecture])
  const ids = (stations || cars || []).map(({_id}) => String(_id))
  const publicViews = usePublicViews()
  const [filteredPublicViews, setFilterPublicViews] = useState([])
  useEffect(() => {
    const publicViewByCategory: {[key: string]: IPublicView[]} = {}
    const views = publicViews.filter(
      ({prefecture: currPrefecture, location}: IPublicView) => {
        if (neLat && neLng && swLat && swLng) {
          return !(
            !location || location[0] > neLng || location[0] < swLng
                  || location[1] > neLat || location[1] < swLat
          )
        }
        return !prefecture && !area
              || area && stationType === StationType.car && prefectureToArea[getPrefecture(Locale.ja, prefecture as PrefectureType)] !== area
              || prefecture && stationType !== StationType.car && currPrefecture !== prefecture
      }
    )
    for (const publicView of views) {
      if (!publicViewByCategory[publicView.category]) publicViewByCategory[publicView.category] = []
      publicViewByCategory[publicView.category].push(publicView)
    }
    let newPublicViews: IPublicView[] = []
    for (
      const values of Object.values(publicViewByCategory)
    ) newPublicViews = [...newPublicViews, ...values.slice(0, 10)]
    setFilterPublicViews(newPublicViews)
  }, [area, neLat, neLng, prefecture, publicViews, swLat, swLng])
  const searchConditionType = carConditionTypeList.filter(cond => query[cond])?.[0]
  const intl = useIntl()
  const locationTitle = !area || area === WHOLE_LOCATION
    ? intl.formatMessage({id: 'stationsSearch.allCountry'})
    : area === CURRENT_LOCATION
      ? intl.formatMessage(messages.stationSearchCurrentLocation)
      : prefecture !== undefined
        ? getPrefecture(locale, prefecture)
        : intl.formatMessage(areaMessages[area as AreaType])
  const queryDateStr = (checkIn && checkOut)
    ? `${intl.formatDate(checkIn, monthAndDay)} - ${intl.formatDate(checkOut, monthAndDay)}`
    : (checkIn && !checkOut) ? `${intl.formatDate(checkIn, monthAndDay)} -　　　　`
      : (!checkIn && checkOut) ? `　　　　- ${intl.formatDate(checkOut, monthAndDay)}`
        : '~'
  const advancedFilterLabel = intl.formatMessage({id: 'cars.search'})
  const switchModeLabel = intl.formatMessage(messages.switchModeLabel)
  const [selectStation, setSelectStation] = useState<IStation | ICar | undefined>(undefined)
  const [stationChangeCount, setStationChangeCount] = useState(0)
  const stationId = selectStation?._id
  const refCount = useRef(0)
  const userType = useUserType()
  useEffect(() => {
    if (stationId) {
      refCount.current += 1
      setStationChangeCount(count => count + 1)
    }
  }, [stationId])
  if (stationType !== StationType.car) {
    // eslint-disable-next-line no-unused-vars
    const facilities = Object.entries(query).filter(([_key, value]) => String(value).toLowerCase() === 'true')
    const seoPage = page !== 0 ? page + 1 : undefined
    const facilityName = facilities.length === 1 && facilities[0][0]
    const canonicalQuery = queryString.stringify(
      {
        ...facilityName && query,
        page: !facilityName ? seoPage : undefined,
      }
    )
    if (facilityName) {
      if (freeFacilityListMsg[facilityName]) {
        const freeFacility = intl.formatMessage(freeFacilityListMsg[facilityName])
        facilityMessage.current = `(${intl.formatMessage(optionMessages.freeFacility, {freeFacility})})`
      } else if (nonFreeFacilityListMsg[facilityName]) {
        const nonFreeFacility = intl.formatMessage(nonFreeFacilityListMsg[facilityName])
        facilityMessage.current = `(${intl.formatMessage(optionMessages.nonFreeFacility, {nonFreeFacility})})`
      } else if (nearbyFacilityListMsg[facilityName]) {
        const nearbyFacility = intl.formatMessage(nearbyFacilityListMsg[facilityName])
        facilityMessage.current = `(${intl.formatMessage(optionMessages.nearbyFacility, {nearbyFacility})})`
      }
    } else {
      facilityMessage.current = ''
    }
    const canonicalQueryString = canonicalQuery ? `?${canonicalQuery}` : ''
    const options = [
      ...(facilityMessage.current ? [facilityMessage.current] : []),
      ...(seoPage ? [`(${
        intl.formatMessage(optionMessages.page, {page: seoPage})
      })`] : []),
    ].join(' ')
    const {
      publicView: publicViewId,
      area: stationArea
    } = stationsStateFromProps(match.params, area)
    const selectedPublicView = useMemo(() => {
      if (!selectStation && publicViewId) for (const publicView of publicViews) if (String(publicView._id) === publicViewId) return publicView
    }, [publicViews, publicViewId, selectStation])
    const cdn = getGlobalEnv('CDN_BASE_URL')
    useSEO(selectedPublicView
      ? publicViewSEO(cdn, true, locale, selectedPublicView)
      : {
        description: stationArea && areaPageDescriptionMessages[stationArea]
          ? intl.formatMessage(areaPageDescriptionMessages[stationArea])
          : intl.formatMessage(messages.japanSearchDescription),
        title: prefecture
          ? intl.formatMessage(
            messages.stationPrefectureTitleWithOptions,
            { prefecture: getPrefecture(locale, prefecture), options }
          )
          : area === CURRENT_LOCATION
            ? intl.formatMessage(messages.stationCurrentLocationSearchTitleWithOptions, { options })
            : !area || area === WHOLE_LOCATION ? intl.formatMessage(messages.japanSearchTitleWithOptions, { options })
            // @ts-expect-error
              : intl.formatMessage(areaPageTitleWithOptionsMessages[area], { options }),
        canonicalUrl: prefecture
          ? `${getGlobalEnv('BASE_URL')}/${locale}/stations/${area}/${area === AreaType.hokkaido && prefecture === PrefectureType.hokkaido ? '' : `${prefecture}/`}${canonicalQueryString}`
          : area === CURRENT_LOCATION
            ? `${getGlobalEnv('BASE_URL')}/${locale}/stations/${canonicalQueryString}`
            : !area || area === WHOLE_LOCATION ? `${getGlobalEnv('BASE_URL')}/${locale}/stations/${canonicalQueryString}`
              : `${getGlobalEnv('BASE_URL')}/${locale}/stations/${area}/${canonicalQueryString}`
      })
  } else {
    // eslint-disable-next-line no-unused-vars
    const facilities = Object.entries(query).filter(([_key, value]) => String(value).toLowerCase() === 'true')
    const seoPage = page !== 0 ? page + 1 : undefined
    let freeFacility
    let nonFreeFacility
    const facilityName = facilities.length === 1 && facilities[0][0]
    if (carCompany || keyword) {
      facilityMessage.current = `（${carCompany || keyword}）`
    } else if (facilityName) {
      // @ts-expect-error
      freeFacility = carFreeFacilityMessages[facilityName] && intl.formatMessage(carFreeFacilityMessages[facilityName])
      // @ts-expect-error
      nonFreeFacility = !freeFacility && carNonFreeFacilityMessages[facilityName] && intl.formatMessage(carNonFreeFacilityMessages[facilityName])
      facilityMessage.current = freeFacility
        ? `(${intl.formatMessage(optionMessages.freeFacility, {freeFacility})})`
        : `(${intl.formatMessage(optionMessages.nonFreeFacility, {nonFreeFacility})})`
    } else {
      facilityMessage.current = ''
    }
    const canonicalQuery = queryString.stringify(
      {
        ...(keyCanonical ? {keyword: keyCanonical} : {}),
        ...facilityName && !keyword && query,
        page: !facilityName ? seoPage : undefined,
      }
    )
    const canonicalQueryString = canonicalQuery ? `?${canonicalQuery}` : ''
    const options = [
      ...(facilityMessage.current ? [facilityMessage.current] : []),
      ...(seoPage ? [`(${
        intl.formatMessage(optionMessages.page, {page: seoPage})
      })`] : []),
    ].join(' ')
    useSEO({
      title:
        prefecture
          ? intl.formatMessage(
            messages.carPrefectureTitleWithOptions,
            { prefecture: getPrefecture(locale, prefecture), options }
          )
          : area === CURRENT_LOCATION
            ? intl.formatMessage(messages.carCurrentLocationSearchTitleWithOptions, { options })
            : !area || area === WHOLE_LOCATION ? intl.formatMessage(messages.carJapanSearchTitleWithOptions, { options })
              // @ts-expect-error
              : intl.formatMessage(carAreaPageTitleWithOptionMessages[area], { options }),
      description: prefecture
        ? intl.formatMessage(
          messages.carPrefectureDescription,
          { prefecture: getPrefecture(locale, prefecture) || intl.formatMessage(messages.stationSearchCurrentLocation) }
        )
        : area === CURRENT_LOCATION
          ? intl.formatMessage(messages.carCurrentLocationSearchDescription)
          : !area || area === WHOLE_LOCATION ? intl.formatMessage(messages.carJapanSearchDescription)
            // @ts-expect-error
            : intl.formatMessage(carAreaPageDescriptionMessages[area]),
      canonicalUrl: prefecture
        ? `${getGlobalEnv('BASE_URL')}/${locale}/car-list/${area}/${prefecture}/${canonicalQueryString}`
        : area === CURRENT_LOCATION
          ? `${getGlobalEnv('BASE_URL')}/${locale}/car-list/${canonicalQueryString}`
          : !area || area === WHOLE_LOCATION ? `${getGlobalEnv('BASE_URL')}/${locale}/car-list/${canonicalQueryString}`
            : `${getGlobalEnv('BASE_URL')}/${locale}/car-list/${area}/${canonicalQueryString}`
    })
  }
  useEffect(() => {
    if (mode === ListMode.map && zoom === defaultMapZoomLevel && (area === CURRENT_LOCATION || prefecture === CURRENT_LOCATION) && total === 0) {
      if (maps) {
        maps.setZoom(defaultMapZoomOutLevel)
      }
    }
  }, [zoom, total, mode, maps, area, prefecture])

  const isMOBIINN = facilityMessage.current.trim().replace(/[^A-Z]/g, '').toUpperCase() === 'MOBIINN'

  const isWebview = useContext(StaticStateContext)!.isWebview || !useIsSsr() && isNativeWebviewVersion2() || isNativeWebview()

  const DescriptionAdvancedSearch = () => {
    const keyAdvancedSearch = carTypes.find(({nameCategory}) => nameCategory === keyword)
    return <>
      {!keyAdvancedSearch ? <div />
        : <>
            <FormattedHTMLMessage id={keyAdvancedSearch.description} />
            <br/>
          </>
      }
    </>
  }

  return <div className={[
    'd-flex flex-column',
    mode === ListMode.map && 'position-fixed position-md-relative',
    styles.fullHeight
  ].join(' ')}>
  {!isWebview && <><MapListNavBar
    mode={mode}
    onListToggle={toggleListMode}
  />
  <NavBarPadding /></>}
  {mode !== ListMode.map && <>
    <Breadcrumb>
      <Link to={`/${locale}/`}>
        <FormattedMessage id="navbar.home"/>
      </Link>
      {stationType === StationType.car && paramsArea !== WHOLE_LOCATION &&
      <Link to={`/${locale}/car-list/`}>
        <FormattedMessage id="breadcrumb.carsAllCountryWithOption" values={{option: facilityMessage.current}} defaultMessage="Rental Car Share Camping Cars Nationwide" />
      </Link>
      }
      {stationType !== StationType.car && paramsArea !== WHOLE_LOCATION &&
      <Link to={`/${locale}/stations/`}>
        <FormattedMessage id="breadcrumb.stations" defaultMessage="車中泊スポット"/>
      </Link>
      }
      {prefecture && getAreaLink(locale, stationType, area, paramsArea, facilityMessage.current)}
      {prefecture === undefined && (paramsArea === CURRENT_LOCATION
        ? stationType === StationType.car
          ? <FormattedMessage id="breadcrumb.carsCurrentLocationWithOption" values={{option: facilityMessage.current}}/>
          : <FormattedMessage id="breadcrumb.stationsCurrentLocationWithOption" values={{option: facilityMessage.current}}/>
        : paramsArea === WHOLE_LOCATION
          ? stationType === StationType.car
            ? <FormattedMessage id="breadcrumb.carsAllCountryWithOption" values={{option: facilityMessage.current}}/>
            : <FormattedMessage id="breadcrumb.stationsAllCountryWithOption" values={{option: facilityMessage.current}}/>
          : area
            ? stationType === StationType.car
              ? <FormattedMessage id="breadcrumb.carsAreaWithOption" values={{option: facilityMessage.current, area: <FormattedMessage {...areaMessages[paramsArea as AreaType]}/>}} />
              : <FormattedMessage id="breadcrumb.stationsAreaWithOption" values={{option: facilityMessage.current, area: <FormattedMessage {...areaMessages[paramsArea as AreaType]}/>}} />
            : <FormattedMessage id="navbar.vs" defaultMessage="Search a car" />)
      }
      {(prefecture && stationType === StationType.car) && <FormattedMessage id="breadcrumb.carsPrefectureWithOption" values={{option: facilityMessage.current, prefecture: getPrefecture(locale, paramsPrefecture as PrefectureType)}} defaultMessage="Rental Car Share Camping Cars in {prefecture}"/>}
      {(prefecture && stationType !== StationType.car) && <FormattedMessage id="breadcrumb.stationsPrefectureWithOption" values={{option: facilityMessage.current, prefecture: getPrefecture(locale, paramsPrefecture as PrefectureType)}} defaultMessage="Staying overnight in {prefecture}" /> }
    </Breadcrumb>
  </>}
  <div className="d-flex justify-content-center flex-grow-1">
  <div className={mode !== ListMode.map ? 'container py-md-5 py-4' : ['d-none d-lg-block pl-2 pt-2 pb-2 col-md-6 overflow-auto', styles.panelVh, 'hidden-scroll'].join(' ')}>
      <div className="bg-white pb-5">
        <div className={[mode === ListMode.map && 'd-flex justify-content-between'].join(' ')}>
          {stationType === StationType.car ? <>
            <h1 className={
              ['pl-12px font-size-30 font-weight-medium', mode === ListMode.map ? 'pt-32px pb-36px' : 'pb-3 text-center']
                .filter(Boolean).join(' ')
            }>
              {searchConditionType
                ? <>#<FormattedMessage {...carConditionTypeMessages[searchConditionType]}/></>
                : longRental
                  ? <>#<FormattedMessage id="car.longRentalCar"/></>
                  : airConditioner
                    ? <>#<LocaleText ja="夏冬も快適" en="With Air Conditioner"/></>
                    : paramsPrefecture
                      ? <FormattedMessage id="cars.carListByAreaWithOptions" values={{area: getPrefecture(locale, paramsPrefecture as PrefectureType), option: facilityMessage.current }} />
                      : area || paramsArea
                        ? area === CURRENT_LOCATION || paramsArea === CURRENT_LOCATION
                          ? <FormattedMessage id="cars.nearbyCars" defaultMessage="List of nearby cars"/>
                          : paramsArea === WHOLE_LOCATION
                            ? isMOBIINN ? <LocaleText ja="トヨタモビリティ東京“初”バンライフカーのレンタルサービス「MOBI-INN」" en="Toyota Mobility Tokyo 'First' van life car rental service 'MOBI-INN'"/>
                              : <FormattedMessage id="cars.carListWithOptions" defaultMessage="List of cars in all areas" values={{ option: facilityMessage.current }}/>
                            : <FormattedMessage {...areaMessages[paramsArea as AreaType]}>
                                  {msg => <FormattedMessage id="cars.carListByAreaWithOptions"
                                    defaultMessage="List of cars in {area}"
                                    values={{ area: msg, option: facilityMessage.current }}
                                  />}
                                </FormattedMessage>
                        : isMOBIINN ? <LocaleText ja="トヨタモビリティ東京“初”バンライフカーのレンタルサービス「MOBI-INN」" en="Toyota Mobility Tokyo 'First' van life car rental service 'MOBI-INN'"/>
                          : <FormattedMessage id="cars.carListWithOptions" defaultMessage="List of cars in all areas"
                              values={{ option: facilityMessage.current }}/>}
            </h1>
            {mode !== ListMode.map && <p className="text-center pb-2 mb-4">
            {searchConditionType === CarConditionType.forBeginner
              ? <>
                  <LocaleText ja="車体サイズが全長5000・全幅2200・全高2850mm以下 / AT免許で運転可 / ガソリンタイプがレギュラー / ETC・バックカメラ・カーナビがついている　など初めてのキャンピングカー運転におすすめの車です。"
                              en="The car body size is 5000 mm overall length, 2200 mm overall width, 2850 mm or less overall height/can be driven with an AT license/the gasoline type is recommended for the first time driving a camper with regular/ETC, back camera, car navigation, etc."/>
                  <br/>
                </>
              : searchConditionType === CarConditionType.familyFriendly
                ? <>
                  <LocaleText ja="3人就寝以上 / ベビーシート・ジュニアシートがついている / 車内飲食可　などお子さま連れのご家族でも安心して旅ができる車両です。"
                              en="For 3 persons sleeping or more/with baby seat and junior seat/food and drink in the car, even families with children can travel safely."/>
                  <br/>
                </>
                : searchConditionType === CarConditionType.campingOutside
                  ? <>
                      <LocaleText ja="アウトドアギアをホルダーからレンタルすることができ、気軽にキャンプを楽しむことができる車両です。"
                                  en="You can rent outdoor gear from the holder, and you can enjoy camping easily."/>
                      <br/>
                    </>
                  : searchConditionType === CarConditionType.petAllowed
                    ? <>
                        <LocaleText ja="ペットと一緒に旅ができる車両です。"
                                    en="It is a car that you can travel with your pet."/>
                        <br/>
                      </>
                    : keyword === 'デートにおすすめ'
                      ? <>
                            <LocaleText ja="就寝人数2名で、写真映えするデートにおすすめの車両です。"
                                        en="It is a car recommended for a photogenic date with 2 people sleeping."/>
                            <br/>
                          </>
                      : keyword === '写真を撮るなら'
                        ? <>
                            <LocaleText ja="外見がおしゃれなキャンピングカーや、内装がDIYでこだわりのある車両です。写真をたくさん撮りたい旅におすすめの車両です。"
                                        en="They are cars with stylish appearance and cars with DIY interior. It is recommended for a trip that looks good in photos."/>
                            <br/>
                          </>
                        : keyword === '友だちとワイワイ'
                          ? <>
                              <LocaleText ja="就寝人数4名以上の大人数の旅におすすめの車両です。"
                                          en="This car is recommended for a large group of 4 or more people sleeping."/>
                              <br/>
                            </>
                          : keyword === 'お手ごろ価格'
                            ? <>
                                <LocaleText ja="24時間¥16,000以下で予約できるお手ごろ価格の車両です。"
                                            en="It is a car with a reasonable price that you can reserve for 24 hours for 16,000 yen or less."/>
                                <br/>
                              </>
                            : keyword === 'スタッドレスタイヤ着用'
                              ? <>
                                  <LocaleText ja="スタッドレスタイヤ着用で、冬も安心に旅できる車両一覧です！冬の運転・車中泊の注意点まとめは"
                                              en="It is a car with a reasonable price that you can reserve for 24 hours for 16,000 yen or less."/>
                                  <Link to={`/${locale}/media/stay/winter_rv/`}
                                      target="_blank">
                                    <LocaleText ja="こちら" en="here"/>
                                    <ExternalLink className="fa ml-1 mb-1"/>
                                  </Link>
                                  <br/>
                                </>
                              : keyword === '大人気車両'
                                ? <>
                                    <LocaleText ja="ゲストからの予約が多い人気車両一覧です。レビューの評価も高く、はじめてのキャンピングカー旅にもおすすめです！Carstayをはじめてご利用いただく方は、"
                                                en="This is a list of popular cars with many reservations from guests. The reviews are high, and it is also recommended for your first camping car trip! If you're new to Carstay, check out our"/>
                                    <Link to={`/${locale}/usage/`}
                                        target="_blank">
                                      <LocaleText ja="ご利用ガイド" en="guide"/>
                                      <ExternalLink className="fa ml-1 mb-1"/>
                                    </Link>
                                    <LocaleText ja="もぜひご覧ください！"
                                                en="!"/>
                                    <br/>
                                  </>
                                : keyword === 'VANLIFE SAUNA'
                                  ? <>
                                        <LocaleText ja="車内に大型薪ストーブを搭載した自走型サウナカー「SAUNA JUNKY零号」と、手軽にアウトドアサウナを楽しめる、テントサウナ付きのコラボレーションカーをカーシェアで提供しています。"
                                                    en='We offer a self-propelled sauna car "SAUNA JUNKY Zero" equipped with a large wood-burning stove in the car, and a collaboration car with a tent sauna in which you can easily enjoy an outdoor sauna.'/>
                                        <br/>
                                      </>
                                  : keyword === 'VANLIFE DJ'
                                    ? <>
                                          <LocaleText ja="VANLIFE DJは、 バンライフとDJを組み合わせた、場所に捉われない、新しい音楽体験です！Carstayでは、DJブースやミラーボールまでワンパッケージになった コンセプトカー「VANLIFE DJ CAR」と、手軽にDDJ-200を楽しめる コラボレーションカーをカーシェアで提供しています。"
                                                      en='VANLIFE DJ is a new, location-agnostic music experience combining van life and DJ! Carstay offers the concept car "VANLIFE DJ CAR" which comes in one package including DJ booth and mirror ball, and the collaboration car in which you can easily enjoy the DJ -200.'/>
                                          <br/>
                                        </>
                                    : keyword === 'VANLIFE Go Pro'
                                      ? <>
                                            <LocaleText ja="GoPro VANLIFEは、バンライフの楽しい思い出や美しい景色をGoProに記録し共有するプロジェクトです！Carstayでは、最新機種のGoPro HERO 10を無料でレンタルできる、キャンピングカーをカーシェアで提供しています。"
                                                        en='GoPro VANLIFE is a project to record and share fun memories and beautiful scenery of van life on GoPro! Carstay offers a car-share camper for renting the latest GoPro HERO 10 for free.'/>
                                            <br/>
                                          </>
                                      : longRental
                                        ? <>
                                            <LocaleText ja="3連泊以上の予約で、利用料が割引になる車両です。※割引率や予約方法の詳細は"
                                                        en="There are cars that you can get a discount on the usage fee if you make a reservation for 3 or more consecutive nights. ※ Discount rate and reservation details are"/>
                                            <a
                                              href="https://moored-speedboat-fc6.notion.site/3e925dc2dcbf45a791a2338702ffc0da"
                                              target="_blank"
                                            >
                                              <LocaleText ja="こちら" en="here"/>
                                              <ExternalLink className="fa ml-1 mb-1"/>
                                            </a>
                                            <br/>
                                          </>
                                        : airConditioner
                                          ? <>
                                            <LocaleText ja="家庭用エアコンとサブバッテリー付きの車両です。エンジンを切ったまま、快適に過ごせます。"
                                                        en="There are cars with home air conditioners. You can stay cool with an air conditioner while the engine is turned off at a site with a power source."/>
                                            <br/>
                                          </>
                                          : <DescriptionAdvancedSearch />
            }
            { isMOBIINN && <Mobiinn /> }
            <FormattedMessage id="cars.carSearch"
                              defaultMessage='If you would like to do a detailed search from basic or additional facilities, press the "Detailed Search" button.'/>
          </p>}
          </> : <>
            <h1 className={mode === ListMode.map ? ['pl-12px pt-32px pb-36px font-size-32 text-left font-weight-medium'].join(' ') : ['pl-12px font-size-30 font-weight-medium pb-3 text-center'].join(' ')}>
              {keyword
                ? <>#{keyword}</>
                : paramsPrefecture
                  ? <FormattedMessage
                    id="stations.stationListByPrefectureWithOption"
                    defaultMessage="List of stations in {prefecture}"
                    values={{ option: facilityMessage.current, prefecture: getPrefecture(locale, paramsPrefecture as PrefectureType)}}
                  />
                  : paramsArea
                    ? paramsArea === CURRENT_LOCATION
                      ? <FormattedMessage id="stations.nearbyStationsWithOption" defaultMessage="List of nearby stations" values={{ option: facilityMessage.current }}/>
                      : paramsArea === WHOLE_LOCATION
                        ? <FormattedMessage id="stations.stationsListWithOption" defaultMessage="List of stations in all prefectures" values={{ option: facilityMessage.current }}/>
                        : <FormattedMessage {...areaMessages[paramsArea as AreaType]}>
                          {msg => <FormattedMessage id="stations.stationListByAreaWithOption"
                                                    defaultMessage="List of station in {area}"
                                                    values={{ option: facilityMessage.current, area: msg }}
                          />}
                        </FormattedMessage>
                    : <FormattedMessage id="stations.stationsListWithOption" defaultMessage="List of stations in all prefectures" values={{ option: facilityMessage.current }}/>
              }
            </h1>
          </>}
          {mode === ListMode.map && <div className="d-flex align-items-center">
            <a
              href="https://persistent-direction-0f1.notion.site/333beeb0fe76453892cbef5f7456a12f"
              target="_blank"
              className={[
                'd-inline-block pt-1 font-size-12 underline text-right',
                stationType !== StationType.car ? 'text-primary' : 'text-van'
              ].join(' ')}
            >
              <Question className="fa mb-1 mr-1"/>
              <LocaleText ja="現在地で検索できない場合" en="If you cannot search from your current location"/>
            </a>
          </div>}
        </div>
        {stationType === StationType.car ? <CarsSearchInput
          mode={mode}
          area={paramsArea}
          prefecture={paramsPrefecture}
          locale={locale}
          checkIn={checkIn}
          checkOut={checkOut}
          onSearchSubmit={onSearchSubmit}
          openSearch={openAdvancedSearch}
        /> : <StationsSearchInput
          mode={mode}
          area={paramsArea}
          prefecture={paramsPrefecture}
          locale={locale}
          checkIn={checkIn}
          checkOut={checkOut}
          onSearchSubmit={onSearchSubmit}
          openSearch={openAdvancedSearch}
        />}
        <TagButtons
          locale={locale}
          area={paramsArea}
          prefecture={paramsPrefecture}
          stationType={stationType}
          mode={mode}
          query={urlParamQueryStringRef.current}
        />
        <div className="d-md-none mb-3"/>
        <ListView
          mode={mode}
          area={area}
          prefecture={prefecture}
          loading={loading}
          listViewType={stationType === StationType.car ? ListViewType.car : ListViewType.station}
          carOrStationList={stations || cars}
          total={total}
          pageSize={pageSize}
          toggleListMode={toggleListMode}
        />
      </div>
    </div>
    {mode === ListMode.map && <div className={['col-lg-6 p-0', styles.panelVh].join(' ')}>
      <Maps
        initialZoom={initialZoom}
        showCurrent
        onInit={mapsInit}
        initialCenter={initialCenter}
        onDemandShow={false}
        initialShow={true}
        refreshCustomizedButtons={refreshCustomizedButtons}
        customizedButtons={[
          {
            position: GoogleMapControlPosition.TOP_CENTER,
            node: <MapSearchFilterPanel
              onSearchOpen={openBasicSearch}
              onAdvancedSearchOpen={openAdvancedSearch}
              locationStr={locationTitle}
              queryDateStr={queryDateStr}
              advancedFilterLabel={advancedFilterLabel}
              paramsArea={paramsArea}
              prefecture={paramsPrefecture}
              stationType={stationType}
              locale={locale}
            />
          },
          {
            position: GoogleMapControlPosition.BOTTOM_CENTER,
            node: <ToggleListModePanel toggleListMode={toggleListMode} switchModeLabel={switchModeLabel}/>
          }
        ]}
      >
        {maps => <>
          {stationType !== StationType.car && <MapsPublicViews
                maps={maps}
                publicViews={filteredPublicViews}/>}
          {stationType !== StationType.car && <MapsStations
            maps={maps}
            zoom={zoom}
            firstSelection={stationChangeCount === 1}
            onChange={station => { setSelectStation(station) }}
            initialSelected={selectStation as IStation}
            initStations={stations}
            filter={(station: IStation) => ids.includes(String(station._id))}
          />}
          {stationType === StationType.car && <MapsCars
            maps={maps}
            cars={cars}
            area={area}
            zoom={zoom}
            prefecture={prefecture}
            firstSelection={stationChangeCount === 1}
            initialSelected={selectStation as ICar}
            onChange={car => { setSelectStation(car) }}
          />}
        </>}
      </Maps>
    </div>}
  </div>
  <TransitionGroup component={null}>
    {searchOpen && <FadeTransition>
      {
        stationType === StationType.car
          ? <CarsSearch onClose={closeSearch} />
          : <StationsSearch onClose={closeSearch} />
      }
    </FadeTransition>}
    {basicSearchOpen && <FadeTransition>
      <BasicSearch
        stationType={stationType}
        onClose={closeBasicSearch}
        onSearchSubmit={onSearchSubmit}
        locale={locale}
        area={area}
        prefecture={prefecture}
      />
    </FadeTransition>}
  </TransitionGroup>
  {mode !== ListMode.map && <Footer />}
</div>
}
